import { PaginationResponse } from 'modules/_shared/components/Pagination/Pagination';
import { useCallback, useEffect, useState } from 'react';
import { store } from 'redux/store';
import { Partner } from 'types';
import { useSelector } from 'react-redux';
import { State } from 'redux/initialState';
import { isPlatformAXA } from 'utils/showDetails';
import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
} from 'modules/partners/pages/PartnerList/constants/sortFieldMap';
import { getPartners } from '../redux/partnerActions';

type UseGetPartnersProps = {
  userId?: string | undefined;
  societyId: string | undefined;
  partnerId?: string | undefined;
  categoryId?: string | undefined;
  page?: number;
  items: number;
  sortBy?: string;
  sortOrder?: string;
  searchTerm?: string;
  /** Increment to refetch without changing other params (e.g. after a mutation). */
  refreshKey?: number;
};

type PartnersResponse = {
  data: Partner[];
  pagination: PaginationResponse;
};

export const useGetPartners = ({
  userId,
  societyId,
  partnerId,
  categoryId,
  page = 1,
  items,
  searchTerm = '',
  sortBy = DEFAULT_SORT_BY,
  sortOrder = DEFAULT_SORT_ORDER,
  refreshKey = 0,
}: UseGetPartnersProps) => {
  const { user } = useSelector((state: State) => state);
  const { isAdmin, isPartner } = useSelector(
    (state: State) => state?.society?.role || {}
  );

  const [data, setData] = useState<Partner[]>([]);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchPartners = useCallback(async () => {
    if (!societyId || partnerId) return;
    if (!isPartner && !isAdmin) return;
    if (!user) return;

    setIsLoading(true);
    setIsError(false);

    try {
      const params: any = {
        ...(userId && { userId }),
        page,
        items,
        sortBy,
        sortOrder,
        searchTerm,
      };

      if (categoryId) {
        params.categoryId = categoryId;
      }

      // TODO: Validate the use case for this (it's blocking Delegation of votes)
      if (
        isPartner &&
        !isAdmin &&
        user?.partners?.length > 0 &&
        isPlatformAXA
      ) {
        params.partnerIds = user?.partners;
      }

      const response = (await store.dispatch(
        getPartners(societyId, params)
      )) as PartnersResponse;

      setData(response.data);
      setPagination(response.pagination as PaginationResponse);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    societyId,
    partnerId,
    page,
    items,
    searchTerm,
    refreshKey,
    sortBy,
    sortOrder,
    categoryId,
    userId,
    isPartner,
    isAdmin,
    user,
  ]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  return {
    data,
    pagination,
    isLoading,
    isError,
  };
};
